import React from 'react';
import { Challenge } from '../../types';
import { Card, CardHeader, CardBody } from '../UI/Card';
import { Badge } from '../UI/Badge';
import styles from './ChallengeCard.module.css';

export interface ChallengeCardProps {
  challenge: Challenge;
  onToggleComplete: (id: string) => void;
  isLoading?: boolean;
}

/**
 * ChallengeCard component displays a challenge with completion status
 * Features:
 * - Fire accent styling when completed
 * - Checkmark icon for completion status
 * - Loading state during S3 updates
 * - Mono font for specs and acceptance criteria
 * - Metallic border for spec boxes
 */
export const ChallengeCard: React.FC<ChallengeCardProps> = ({
  challenge,
  onToggleComplete,
  isLoading = false,
}) => {
  const handleClick = () => {
    if (!isLoading) {
      onToggleComplete(challenge.id);
    }
  };

  const cardClassName = `${styles.challengeCard} ${
    challenge.isCompleted ? styles.completed : ''
  } ${isLoading ? styles.loading : ''}`;

  return (
    <Card className={cardClassName} onClick={handleClick}>
      <CardHeader>
        <div className={styles.header}>
          <h3 className={styles.title}>{challenge.title}</h3>
          <div className={styles.statusIndicator}>
            {challenge.isCompleted && (
              <Badge variant="cyan" className={styles.completedBadge}>
                <span className={styles.checkmark}>✓</span>
                Completed
              </Badge>
            )}
            {isLoading && (
              <span className={styles.loadingText}>Saving...</span>
            )}
          </div>
        </div>
      </CardHeader>

      <CardBody>
        <p className={styles.description}>{challenge.description}</p>

        {challenge.rules && challenge.rules.length > 0 && (
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Rules</h4>
            <div className={styles.specBox}>
              <ul className={styles.list}>
                {challenge.rules.map((rule, index) => (
                  <li key={index} className={styles.listItem}>
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {challenge.deliverables && challenge.deliverables.length > 0 && (
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Deliverables</h4>
            <div className={styles.specBox}>
              <ul className={styles.list}>
                {challenge.deliverables.map((deliverable, index) => (
                  <li key={index} className={styles.listItem}>
                    {deliverable}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {challenge.rewards && (
          <div className={styles.section}>
            <h4 className={styles.sectionTitle}>Rewards</h4>
            <Badge variant="fire" className={styles.rewardBadge}>
              {challenge.rewards}
            </Badge>
          </div>
        )}
      </CardBody>
    </Card>
  );
};
